// @ts-nocheck
// Shared generation-dispatch hook.
//
// Extracted from ImageGenerationContext so BOTH the classic single-workflow
// workbench AND the Multi (parallel multi-model) section share ONE dispatch
// implementation — no duplication, identical enhance/pipeline/async behavior.
//
// The dispatch, polling, and their refs live here; component-specific bits
// (selected collections, the "limit exceeded" modal) are injected via options.
import { useCallback, useEffect, useRef } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useWidgetContext } from '../../../contexts/WidgetContext';
import { saveSentRequest } from '../../../utils/requestHistory';
import { getMediaForSlot, narrowPayloadForSlot } from '../../../utils/requestCalculator';
import { addSentryBreadcrumb } from '../../../utils/sentry';
import { is3DModelUrl } from '../../../utils/mediaTypeDetection';
import { clampSeed, offsetSeed } from '../../../utils/seedRolling';
import type { GalleryMediaItem, SeedPayloadField } from '../../../interfaces/IWorkflowWidget';

const inferContentTypeFromUrl = (rawUrl) => {
  if (!rawUrl) return undefined;
  // Strip query string / hash before matching, exactly as the sibling
  // `galleryUtils.inferContentTypeFromUrl` does: signed asset URLs (fal.media,
  // presigned R2) carry `?token=...`, which an anchored extension regex misses —
  // a video/audio would silently fall through to `image/jpeg` and then get
  // pushed through the image proxy. The 3D branch below was already immune
  // because `is3DModelUrl` strips internally.
  const url = rawUrl.split('?')[0].split('#')[0];
  if (url.match(/\.(mp4|webm|mov|avi|mkv|m4v)$/i)) {
    return 'video/mp4';
  }
  if (url.match(/\.(mp3|wav|ogg|flac|m4a|aac|mpeg)$/i)) {
    return 'audio/mpeg';
  }
  // 3D: `/api/workflow/external-call` returns no `contentTypes` on the sync path,
  // so this fallback is what a freshly dispatched slot carries. Without this
  // branch a `.glb` was labelled `image/jpeg` and every 3D behaviour (placeholder,
  // model-viewer, download naming, hidden WebP export) rested solely on each
  // surface's own `is3DModelUrl` fallback. Returns the bare `'3d'` the DB/gallery
  // rows use — `is3DContentType` accepts it, and no consumer parses this as MIME.
  if (is3DModelUrl(url)) {
    return '3d';
  }
  // Default to image for other extensions or no extension
  return 'image/jpeg';
};

export interface UseGenerationDispatchOptions {
  /** Collections to attach to each request (empty for Multi). */
  selectedCollections?: number[];
  /** Identifies requests launched from Multi so its extra media contract stays route-scoped. */
  generationSource?: 'multi';
  /** Converts stable backend error codes into surface-localized user messages. */
  formatGenerationError?: (error: string) => string;
  /** Called when the backend reports an organization/user limit was exceeded. */
  onLimitExceeded?: (type: 'organization' | 'user') => void;
}

// Module-level so the default is referentially stable: selectedCollections is a
// dep of dispatchGeneration's useCallback, and an inline `= []` would mint a new
// array every render for callers that omit the option — defeating the
// memoization (and re-registering the workbench's Generate keydown listener).
const NO_COLLECTIONS: number[] = [];

export function useGenerationDispatch(options: UseGenerationDispatchOptions = {}) {
  const {
    selectedCollections = NO_COLLECTIONS,
    generationSource,
    formatGenerationError,
    onLimitExceeded,
  } = options;
  const { getToken } = useAuth();
  const { user } = useUser();
  const { currentOrganization } = useOrganization();
  const { widgetState, updateGenerationSlot, addGenerationSlot } = useWidgetContext();

  type PollingTask = {
    timeoutId: NodeJS.Timeout;
    generationId: number;
    retryCount: number;
    lastError?: string;
  };
  const pollingTasksRef = useRef<Map<string, PollingTask>>(new Map());

  // Keep a ref to the current generation slots to avoid stale closures
  const generationSlotsRef = useRef(widgetState.generationSlots);
  useEffect(() => {
    generationSlotsRef.current = widgetState.generationSlots;
  }, [widgetState.generationSlots]);

  // Any organization read AFTER an await must come from a ref — a callback
  // closure freezes `currentOrganization` at creation time, so it can never
  // report a switch that happened while the request was in flight. Same rule as
  // `organizationIdRef` in the media widgets.
  const organizationIdRef = useRef(currentOrganization?.id);
  useEffect(() => {
    organizationIdRef.current = currentOrganization?.id;
  }, [currentOrganization?.id]);

  // Cleanup polling tasks on unmount. isMountedRef also gates the restore
  // timeouts below: restoreActiveGenerations schedules polling via setTimeout,
  // so a restore resolving after unmount would otherwise register a fresh
  // polling chain in this dead instance AFTER this cleanup already ran — and
  // the next page's own restore would then double-poll the same generation.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear all polling tasks
      pollingTasksRef.current.forEach(task => {
        clearTimeout(task.timeoutId);
      });
      pollingTasksRef.current.clear();
    };
  }, []);

  // Async polling function (defined first to avoid circular dependency)
  const startAsyncPolling = useCallback(async (slotId: string, generationId: number, jobId: string) => {
    // Never register a polling chain in an unmounted instance. dispatchGeneration
    // awaits the network, so it can resolve AFTER the user navigated away — the
    // unmount cleanup already drained pollingTasksRef, so anything registered now
    // would never be cleared: `poll` re-arms itself for up to 720 attempts (60 min)
    // against a frozen generationSlotsRef, and the next page's restoreActiveGenerations
    // would poll the same generation a second time (dedupe is per-instance).
    if (!isMountedRef.current) return;
    console.log('🔄 Starting async polling for generation:', { slotId, generationId, jobId });

    // Dedupe per generation — but ONLY while the task's OWN slot is still alive.
    // `WidgetProvider`'s tenant-switch clear wipes `generationSlots` without
    // touching `pollingTasksRef`, so a task outlives its slot by up to one poll
    // interval (it self-deletes on its next tick, on finding the slot gone).
    // `restoreActiveGenerations` deliberately falls through in that window to
    // rebuild the slot; if this guard still counted the doomed task as "active"
    // it would cancel that fix out — the rebuilt slot gets NO poller, then the
    // stale task dies, leaving a DEBITED async generation stuck at `processing`
    // with nothing polling it (tokens never finalized or refunded) until the
    // next remount. So an ORPHANED task is cleared here and a fresh chain is
    // armed under the new slot id.
    const orphanCandidate = Array.from(pollingTasksRef.current.entries()).find(
      ([, task]) => task.generationId === generationId
    );

    if (orphanCandidate) {
      const [ownerSlotId, ownerTask] = orphanCandidate;
      if (generationSlotsRef.current.some(slot => slot.id === ownerSlotId)) {
        console.log('⚠️ Polling already active for generation:', generationId);
        return;
      }
      console.log('♻️ Re-arming polling for orphaned generation:', generationId);
      clearTimeout(ownerTask.timeoutId);
      pollingTasksRef.current.delete(ownerSlotId);
    }

    // Cancel any existing polling for this slot
    const existingTask = pollingTasksRef.current.get(slotId);
    if (existingTask) {
      clearTimeout(existingTask.timeoutId);
      pollingTasksRef.current.delete(slotId);
    }
    
    const pollInterval = 5000; // 5 seconds
    // 1 hour (720 * 5 seconds). Must stay >= the server-side ceiling
    // MAX_POLL_DURATION_MS (60 min) in api/workflow/async-check.ts and
    // api/v2/generations/[id]/check.ts, and the 3600s Dify-side limits.
    // Async progress is driven ONLY by this browser poller (there is no server
    // cron), so giving up earlier than the server does leaves the row stuck in
    // `checking` with nobody left to finalize it — a 40-minute 3D/video job then
    // completes upstream and the site never sees it.
    const maxAttempts = 720;
    const maxPollMinutes = Math.round((maxAttempts * pollInterval) / 60000);
    let attempts = 0;

    // Every re-arm goes through here. `poll` awaits the network, so the component
    // can unmount mid-request: the unmount cleanup has already drained
    // pollingTasksRef, so a timer registered afterwards would NEVER be cleared —
    // poll would re-arm itself for up to 720 attempts (60 min) against a frozen
    // generationSlotsRef, and the next page's restoreActiveGenerations would poll
    // the same generation a second time (the dedupe is per-hook-instance).
    const scheduleNextPoll = (delay: number, task: Omit<PollingTask, 'timeoutId'>) => {
      if (!isMountedRef.current) {
        pollingTasksRef.current.delete(slotId);
        return;
      }
      const timeoutId = setTimeout(poll, delay);
      pollingTasksRef.current.set(slotId, { timeoutId, ...task });
    };

    const poll = async () => {
      // Check if polling was cancelled
      if (!pollingTasksRef.current.has(slotId)) {
        console.log('🛑 Polling cancelled for slot:', slotId);
        return;
      }

      // Unmounted between the timer firing and now — stop instead of updating
      // slots that belong to a dead instance.
      if (!isMountedRef.current) {
        pollingTasksRef.current.delete(slotId);
        return;
      }

      // Check if slot still exists using ref to get current value
      const currentSlot = generationSlotsRef.current.find(s => s.id === slotId);
      if (!currentSlot) {
        console.warn('⚠️ Slot disappeared, stopping polling:', slotId);
        pollingTasksRef.current.delete(slotId);
        return;
      }
      
      if (!currentOrganization?.id) {
        console.error('No organization context for polling');
        pollingTasksRef.current.delete(slotId);
        return;
      }
      
      try {
        const token = await getToken({ template: 'withmail' });
        const response = await fetch('/api/workflow/async-check', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Organization-Id': currentOrganization.id
          },
          body: JSON.stringify({
            generation_id: generationId,
            job_id: jobId
          })
        });
        
        if (!response.ok) {
          // Include status code in error for better retry detection
          throw new Error(`Async check failed: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        console.log('📊 Async check result:', result);
        
        // Extract pipeline progress if available (for multi-step pipelines)
        const pipelineUpdate: Record<string, any> = {};
        if (result.pipeline) {
          pipelineUpdate.pipelineSteps = result.pipeline.steps;
          pipelineUpdate.currentStepIndex = result.pipeline.currentStep;
          pipelineUpdate.totalSteps = result.pipeline.totalSteps;
        }

        if (result.completed) {
          // Job completed, remove from active tasks
          pollingTasksRef.current.delete(slotId);

          if (result.status === 'completed' && result.success) {
            const draftUpdate = typeof result.draft_job_id === 'string' && result.draft_job_id.trim()
              ? { draftJobId: result.draft_job_id.trim() }
              : {};
            // Check for text content type
            if (result.text_content || result.content_types?.[0] === 'text') {
              updateGenerationSlot(slotId, {
                status: 'completed',
                contentType: 'text',
                textContent: result.text_content || '',
                ...draftUpdate,
                ...pipelineUpdate
              });
            } else {
              // Extract ALL URLs for multiple images support
              const previewUrls = result.preview_urls || result.external_urls || [];
              const originalUrls = result.original_urls || result.preview_urls || result.external_urls || [];

              // Use first URL for backward compatibility
              const previewUrl = previewUrls[0] || null;
              const originalUrl = originalUrls[0] || null;

              // Determine content type - prefer URL detection for video/audio (more reliable)
              const urlContentType = inferContentTypeFromUrl(originalUrl);
              const contentType = (urlContentType?.startsWith('video') || urlContentType?.startsWith('audio'))
                ? urlContentType
                : (result.content_types?.[0] || urlContentType);

              updateGenerationSlot(slotId, {
                status: 'completed',
                previewUrl,
                originalUrl,
                previewUrls,
                originalUrls,
                contentType,
                ...draftUpdate,
                ...pipelineUpdate
              });
            }
          } else {
            // Job failed
            updateGenerationSlot(slotId, {
              status: 'failed',
              error: result.error || 'Async job failed',
              ...pipelineUpdate
            });
          }
          return; // Stop polling
        }

        // Still processing — update pipeline progress if available
        if (result.pipeline) {
          updateGenerationSlot(slotId, pipelineUpdate);
        }

        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          // Reset retry count after a successful check
          scheduleNextPoll(pollInterval, { generationId, retryCount: 0 });
        } else {
          // Timeout
          pollingTasksRef.current.delete(slotId);
          updateGenerationSlot(slotId, {
            status: 'failed',
            error: `Generation timeout - exceeded ${maxPollMinutes} minutes`
          });
        }
      } catch (error) {
        console.error('❌ Async polling error:', error);
        
        // Get current task info
        const currentTask = pollingTasksRef.current.get(slotId);
        const retryCount = currentTask?.retryCount || 0;
        
        // Simplified retry logic: retry everything except successful responses with business errors
        // and client errors (4xx) except 408 and 429
        let isRetryableError = true; // Default: retry all errors
        
        // Don't retry only specific cases:
        // 1. Client errors (400-407, 409-428, 430-499) - bad request, unauthorized, etc.
        const statusMatch = error.message.match(/Async check failed: (\d+)/);
        if (statusMatch) {
          const statusCode = parseInt(statusMatch[1]);
          if (statusCode >= 400 && statusCode < 500 && statusCode !== 408 && statusCode !== 429) {
            isRetryableError = false;
          }
        }
        // 2. Explicit business logic failures (handled above in the success path)
        // All other errors (network, 5xx, timeouts, Cloudflare errors) are retryable
        
        if (isRetryableError && retryCount < 3) {
          // Exponential backoff: 1s, 2s, 5s
          const retryDelays = [1000, 2000, 5000];
          const delay = retryDelays[retryCount] || 5000;
          
          // Enhanced logging to track error types
          const errorType = error.message.includes('Async check failed: 5') ? '5xx Server Error' :
                           error.message.includes('Async check failed: 52') ? 'Cloudflare Error' :
                           error.message.includes('fetch') ? 'Network Error' :
                           error.message.includes('timeout') ? 'Timeout' : 'Unknown Error';
          
          console.log(`⚠️ Retry attempt ${retryCount + 1}/3 for slot ${slotId} after ${delay}ms. Type: ${errorType}, Error: ${error.message}`);
          
          // Schedule retry with delay
          scheduleNextPoll(delay, {
            generationId,
            retryCount: retryCount + 1,
            lastError: error.message
          });
        } else {
          // Final failure after all retries or non-retryable error
          console.error(`❌ Final failure for slot ${slotId} after ${retryCount + 1} attempts:`, error);
          pollingTasksRef.current.delete(slotId);
          
          const errorMessage = retryCount > 0 
            ? `${error.message} (failed after ${retryCount + 1} attempts)`
            : error.message || 'Polling failed';
            
          updateGenerationSlot(slotId, {
            status: 'failed',
            error: errorMessage
          });
        }
      }
    };
    
    // Start polling with retry count initialized to 0
    const timeoutId = setTimeout(poll, pollInterval);
    pollingTasksRef.current.set(slotId, { 
      timeoutId, 
      generationId,
      retryCount: 0
    });
  }, [currentOrganization, getToken, updateGenerationSlot]); // Removed widgetState.generationSlots to prevent recreation

  // Dispatch a single generation for one slot.
  //
  // Extracted verbatim from handleGenerateClick's per-slot map body so the Multi
  // (parallel multi-model) section can fan out one dispatch per checked model.
  // Every previous `selectedWorkflow` reference is now the `workflow` parameter;
  // per-slot media (requiredFiles/galleryUrls/blobFileCount) and the base payload
  // are passed in. Pipeline detection, enhance, FormData/JSON branch, and
  // async-polling start all live here — identical behavior to the inline version.
  const dispatchGeneration = useCallback(async ({
    workflow,
    generatePayload,
    requiredFiles,
    preUploadedUrls,
    galleryUrls = [],
    blobFileCount = 0,
    seedFields = [],
    index,
    slotId,
    token,
  }: {
    workflow: any;
    generatePayload: Record<string, any>;
    requiredFiles: File[];
    preUploadedUrls?: string[];
    // Gallery entries are objects ({ url, ... }), not bare strings —
    // getMediaForSlot/narrowPayloadForSlot read `.url` off each item.
    galleryUrls?: GalleryMediaItem[];
    blobFileCount?: number;
    seedFields?: SeedPayloadField[];
    index: number;
    slotId: string;
    token: string;
  }) => {
    const i = index;
    // Clone payload per slot so enhance can modify prompt independently
    const slotPayload = { ...generatePayload };
    seedFields.forEach(({ payloadKey, min, max }) => {
      const baseSeed = slotPayload[payloadKey];
      const numericBaseSeed = typeof baseSeed === 'string' && baseSeed.trim() === ''
        ? Number.NaN
        : Number(baseSeed);
      if (
        Object.prototype.hasOwnProperty.call(slotPayload, payloadKey)
        && Number.isFinite(numericBaseSeed)
      ) {
        slotPayload[payloadKey] = offsetSeed(
          clampSeed(numericBaseSeed, min, max),
          i,
          min,
          max
        );
      }
    });

    // Detect pipeline mode: check explicit pipeline_mode first, then fall back to steps check
    const workflowSteps = workflow.workflow?.settings?.steps;
    const pipelineMode = workflow.workflow?.settings?.pipeline_mode;
    const isPipelineMode = pipelineMode === 'pipeline' ||
      (!pipelineMode && Array.isArray(workflowSteps) && workflowSteps.length > 0);

    try {
      // CRITICAL FIX: In single_per_request mode, each promise should only process ONE media item
      const inputFilesLogic = workflow.workflow?.input_files_logic || 'all_per_request';
      const hasPreUploadedUrls = (preUploadedUrls?.length ?? 0) > 0;

      // Use utility function to determine which media this slot should process
      const mediaForSlot = getMediaForSlot(
        i,
        inputFilesLogic,
        hasPreUploadedUrls ? [] : requiredFiles,
        galleryUrls || []
      );
      const filesToSend = mediaForSlot.files;
      const galleryUrlsToSend = mediaForSlot.galleryUrls;
      const hasGalleryUrls = galleryUrlsToSend.length > 0;

      // In single_per_request mode, narrow custom media keys in payload
      // so the backend only receives URLs for THIS slot's media item.
      //
      // requiredFiles is ordered: blob-associated files first (0..blobFileCount-1),
      // then standard files (blobFileCount..N-1). Only blob-associated files
      // have a corresponding blob URL in the payload.
      if (inputFilesLogic === 'single_per_request') {
        // If this slot processes a blob-associated file, use its index as blobIndexToKeep.
        // If it processes a standard file (no blob URL), use -1 to strip all blobs.
        const blobIndex = (filesToSend.length > 0 && i < blobFileCount) ? i : -1;
        narrowPayloadForSlot(slotPayload, filesToSend, galleryUrlsToSend, blobIndex, galleryUrls || []);
      }

      // Pipeline mode: use /api/workflow/pipeline-execute endpoint
      if (isPipelineMode) {
        // Set initial pipeline step data on the slot
        const initialPipelineSteps = workflowSteps!.map((step, idx) => ({
          name: step.name,
          status: idx === 0 ? 'running' as const : 'pending' as const
        }));

        updateGenerationSlot(slotId, {
          status: 'generating',
          pipelineSteps: initialPipelineSteps,
          currentStepIndex: 0,
          totalSteps: workflowSteps!.length
        });

        // Build request body
        let pipelineRequestBody: FormData | string;
        const pipelineHeaders: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': currentOrganization.id,
          ...(generationSource ? { 'X-Generation-Source': generationSource } : {}),
        };

        if (filesToSend.length > 0) {
          const formData = new FormData();
          formData.append('workflowId', workflow.id);
          formData.append('inputs', JSON.stringify(slotPayload));
          if (selectedCollections.length > 0) {
            formData.append('selectedCollections', JSON.stringify(selectedCollections));
          }
          filesToSend.forEach((file, index) => {
            formData.append(`file_${index}`, file);
          });
          formData.append('fileCount', filesToSend.length.toString());
          if (hasGalleryUrls) {
            formData.append('galleryUrls', JSON.stringify(galleryUrlsToSend));
          }
          pipelineRequestBody = formData;
        } else {
          pipelineHeaders['Content-Type'] = 'application/json';
          pipelineRequestBody = JSON.stringify({
            workflowId: workflow.id,
            inputs: slotPayload,
            selectedCollections: selectedCollections.length > 0 ? selectedCollections : undefined,
            galleryUrls: hasGalleryUrls ? galleryUrlsToSend : undefined,
            preUploadedUrls: hasPreUploadedUrls ? preUploadedUrls : undefined,
          });
        }

        const pipelineResponse = await fetch('/api/workflow/pipeline-execute', {
          method: 'POST',
          headers: pipelineHeaders,
          body: pipelineRequestBody
        });

        const pipelineResult = await pipelineResponse.json();

        console.log('🔄 Pipeline API response:', {
          success: pipelineResult.success,
          async: pipelineResult.async,
          generation_id: pipelineResult.generation_id,
          pipeline: pipelineResult.pipeline
        });

        // Extract pipeline progress from response
        const pipelineSlotData: Record<string, any> = {};
        if (pipelineResult.pipeline) {
          pipelineSlotData.pipelineSteps = pipelineResult.pipeline.steps;
          pipelineSlotData.currentStepIndex = pipelineResult.pipeline.currentStep;
          pipelineSlotData.totalSteps = pipelineResult.pipeline.totalSteps;
        }

        if (pipelineResult.success) {
          if (pipelineResult.async) {
            // Pipeline went async (a step is waiting for callback)
            updateGenerationSlot(slotId, {
              status: 'processing',
              asyncGenerationId: pipelineResult.generation_id,
              asyncJobId: `pipeline-${pipelineResult.generation_id}`,
              rawInput: JSON.stringify(slotPayload),
              ...pipelineSlotData
            });

            // Start polling for pipeline progress
            startAsyncPolling(slotId, pipelineResult.generation_id, `pipeline-${pipelineResult.generation_id}`);

            return { index: i, success: true, async: true, slotId };
          }

          // Pipeline completed synchronously — extract results
          const data = pipelineResult.data;
          if (data) {
            // Check for text content type
            if (data.textContent || data.contentTypes?.[0] === 'text') {
              updateGenerationSlot(slotId, {
                status: 'completed',
                contentType: 'text',
                textContent: data.textContent || '',
                rawInput: JSON.stringify(slotPayload),
                ...pipelineSlotData
              });
              return { index: i, success: true, slotId };
            }

            const isStringArray = (arr: any): arr is string[] =>
              Array.isArray(arr) && arr.every(item => typeof item === 'string');

            const extractImageUrls = (urlData: any[]): string[] => {
              if (!Array.isArray(urlData)) return [];
              return urlData.flatMap(item => {
                if (item.files && Array.isArray(item.files)) return item.files.map((f: any) => f.url).filter(Boolean);
                if (item.images && Array.isArray(item.images)) return item.images.map((img: any) => img.url).filter(Boolean);
                if (item.url && typeof item.url === 'string') return [item.url];
                return [];
              });
            };

            const previewUrls = isStringArray(data.preview) ? data.preview : extractImageUrls(data.preview || []);
            const originalUrls = isStringArray(data.original) ? data.original : extractImageUrls(data.original || []);
            const externalUrls = isStringArray(data.external) ? data.external : extractImageUrls(data.external || []);

            const allPreviewUrls = previewUrls.length > 0 ? previewUrls : externalUrls;
            const allOriginalUrls = originalUrls.length > 0 ? originalUrls : (previewUrls.length > 0 ? previewUrls : externalUrls);
            const previewUrl = allPreviewUrls[0] || null;
            const originalUrl = allOriginalUrls[0] || null;
            const urlContentType = inferContentTypeFromUrl(originalUrl);
            const contentTypes = data.contentTypes || [];
            const contentType = (urlContentType?.startsWith('video') || urlContentType?.startsWith('audio')) ? urlContentType : (contentTypes[0] || urlContentType);

            updateGenerationSlot(slotId, {
              status: 'completed',
              previewUrl,
              originalUrl,
              previewUrls: allPreviewUrls,
              originalUrls: allOriginalUrls,
              contentType,
              rawInput: JSON.stringify(slotPayload),
              ...pipelineSlotData
            });

            return { index: i, success: true, slotId };
          }

          // No data in response but success — shouldn't happen, treat as completed
          updateGenerationSlot(slotId, {
            status: 'completed',
            rawInput: JSON.stringify(slotPayload),
            ...pipelineSlotData
          });
          return { index: i, success: true, slotId };
        }

        // Pipeline failed
        addSentryBreadcrumb('generation', 'Pipeline execution failed', 'error', {
          workflowId: workflow.id,
          error: pipelineResult.error || 'Pipeline execution failed',
        });
        let errorMessage = pipelineResult.error || 'Pipeline execution failed';
        if (errorMessage.includes('ORGANIZATION_LIMIT_EXCEEDED')) {
          onLimitExceeded?.('organization');
        }
        errorMessage = formatGenerationError?.(errorMessage) || errorMessage;

        updateGenerationSlot(slotId, {
          status: 'failed',
          error: errorMessage,
          errorDetails: pipelineResult.errorDetails || undefined,
          ...pipelineSlotData
        });
        return { index: i, success: false, error: errorMessage, slotId };
      }

      // Standard (non-pipeline) mode: Auto prompt enhance step
      // Skip auto_prompt_enhance in pipeline mode — enhancement is a step in the pipeline
      const autoEnhance = workflow.workflow?.settings?.auto_prompt_enhance === true;

      if (autoEnhance) {
        updateGenerationSlot(slotId, { status: 'enhancing' });

        try {
          let enhanceRequestBody: FormData | string;
          const enhanceHeaders: Record<string, string> = {
            'Authorization': `Bearer ${token}`,
            'X-Organization-Id': currentOrganization.id,
            ...(generationSource ? { 'X-Generation-Source': generationSource } : {}),
          };

          if (filesToSend.length > 0) {
            const formData = new FormData();
            formData.append('workflowId', workflow.id);
            formData.append('urlType', 'additional2');
            formData.append('inputs', JSON.stringify(slotPayload));
            filesToSend.forEach((file, idx) => formData.append(`file_${idx}`, file));
            formData.append('fileCount', filesToSend.length.toString());
            if (hasGalleryUrls) formData.append('galleryUrls', JSON.stringify(galleryUrlsToSend));
            enhanceRequestBody = formData;
          } else {
            enhanceHeaders['Content-Type'] = 'application/json';
            enhanceRequestBody = JSON.stringify({
              workflowId: workflow.id,
              urlType: 'additional2',
              inputs: slotPayload,
              galleryUrls: hasGalleryUrls ? galleryUrlsToSend : undefined,
              preUploadedUrls: hasPreUploadedUrls ? preUploadedUrls : undefined,
            });
          }

          const enhanceResponse = await fetch('/api/workflow/external-call', {
            method: 'POST',
            headers: enhanceHeaders,
            body: enhanceRequestBody
          });

          const enhanceResult = await enhanceResponse.json();

          if (enhanceResult.success && enhanceResult.raw?.data?.outputs) {
            const outputs = enhanceResult.raw.data.outputs;
            const DEFAULT_MAPPINGS = [
              { source: 'text', target: 'prompt' },
              { source: 'is_nsfw', target: 'is_nsfw' }
            ];
            const mappings = workflow.workflow?.settings?.enhance_field_mappings || DEFAULT_MAPPINGS;
            let enhancedPrompt: string | undefined;

            for (const { source, target } of mappings) {
              const value = outputs[source];
              if (value !== undefined) {
                slotPayload[target] = value;
                if (target === 'prompt') enhancedPrompt = value;
              }
            }

            if (enhancedPrompt) {
              updateGenerationSlot(slotId, { prompt: enhancedPrompt });
            }
          } else {
            const errorMsg = enhanceResult.externalApiError?.message || enhanceResult.error || 'Prompt enhance failed';
            updateGenerationSlot(slotId, { status: 'failed', error: errorMsg });
            return { index: i, success: false, error: errorMsg, slotId };
          }
        } catch (enhanceError) {
          const errorMsg = enhanceError instanceof Error ? enhanceError.message : 'Prompt enhance failed';
          updateGenerationSlot(slotId, { status: 'failed', error: errorMsg });
          return { index: i, success: false, error: errorMsg, slotId };
        }
      }

      // Re-fetch token if enhance step was performed (it can take 30-60s, expiring the original token)
      let currentToken = token;
      if (autoEnhance) {
        const freshToken = await getToken({ template: 'withmail' });
        if (freshToken) currentToken = freshToken;
      }

      // Update slot to generating status
      updateGenerationSlot(slotId, { status: 'generating' });

      // Use FormData if there are files, otherwise JSON
      let requestBody: FormData | string;
      let contentType: string;

      if (filesToSend.length > 0) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append('workflowId', workflow.id);
        formData.append('urlType', 'main');
        formData.append('inputs', JSON.stringify(slotPayload));
        if (selectedCollections.length > 0) {
          formData.append('selectedCollections', JSON.stringify(selectedCollections));
        }

        // Add each file (now correctly limited to filesToSend)
        filesToSend.forEach((file, index) => {
          formData.append(`file_${index}`, file);
        });
        formData.append('fileCount', filesToSend.length.toString());

        // Add gallery URLs if any (these won't be re-uploaded)
        if (hasGalleryUrls) {
          formData.append('galleryUrls', JSON.stringify(galleryUrlsToSend));
        }

        requestBody = formData;
        contentType = 'multipart/form-data';
      } else {
        // Use JSON for requests without files
        requestBody = JSON.stringify({
          workflowId: workflow.id,
          urlType: 'main',
          inputs: slotPayload,
          selectedCollections: selectedCollections.length > 0 ? selectedCollections : undefined,
          galleryUrls: hasGalleryUrls ? galleryUrlsToSend : undefined,
          preUploadedUrls: hasPreUploadedUrls ? preUploadedUrls : undefined,
        });
        contentType = 'application/json';
      }

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${currentToken}`,
        'X-Organization-Id': currentOrganization.id,
        ...(generationSource ? { 'X-Generation-Source': generationSource } : {}),
      };

      // Only set Content-Type for JSON (let browser set it for FormData)
      if (contentType === 'application/json') {
        headers['Content-Type'] = contentType;
      }

      const response = await fetch('/api/workflow/external-call', {
        method: 'POST',
        headers,
        body: requestBody
      });

      const result = await response.json();

      console.log('🔄 Workflow API response:', {
        success: result.success,
        error: result.error,
        hasData: !!result.data,
        resultKeys: Object.keys(result || {}),
        fullResult: result
      });

      if (result.success && !result.errors) {
        // Save to request history if sentPayload is available
        if (result.sentPayload) {
          saveSentRequest({
            timestamp: Date.now(),
            workflowId: result.sentPayload.workflowId,
            workflowTitle: result.sentPayload.workflowTitle || workflow?.workflow?.title || 'Unknown',
            inputs: result.sentPayload.inputs,
            categorizedUrls: result.sentPayload.categorizedUrls
          });
        }

        // Extract pipeline progress from response if present
        const pipelineSlotData: Record<string, any> = {};
        if (result.pipeline) {
          pipelineSlotData.pipelineSteps = result.pipeline.steps;
          pipelineSlotData.currentStepIndex = result.pipeline.currentStep;
          pipelineSlotData.totalSteps = result.pipeline.totalSteps;
        }

        // Check if this is an async job (single mode: result.async, multi-file mode: result.async_jobs)
        if (result.async) {
          console.log('🔄 Async job detected (single mode), starting polling for slot:', slotId);

          // Update slot to show it's processing async
          updateGenerationSlot(slotId, {
            status: 'processing',
            asyncJobId: result.job_id,
            asyncGenerationId: result.generation_id,
            rawInput: JSON.stringify(result.sentPayload?.inputs || {}),
            ...pipelineSlotData
          });

          // Start polling for this slot
          startAsyncPolling(slotId, result.generation_id, result.job_id);

          return { index: i, success: true, async: true, slotId };
        }

        // Check for async_jobs array (multi-file mode)
        if (result.async_jobs && Array.isArray(result.async_jobs) && result.async_jobs.length > 0) {
          console.log('🔄 Async jobs detected (multi-file mode):', result.async_jobs.length, 'jobs for slot:', slotId);

          // For now, handle only the first async job for this slot
          const asyncJob = result.async_jobs[0];

          // Update slot to show it's processing async
          updateGenerationSlot(slotId, {
            status: 'processing',
            asyncJobId: asyncJob.job_id,
            asyncGenerationId: asyncJob.generation_id,
            rawInput: JSON.stringify(result.sentPayload?.inputs || {}),
            ...pipelineSlotData
          });

          // Start polling for this slot
          startAsyncPolling(slotId, asyncJob.generation_id, asyncJob.job_id);

          return { index: i, success: true, async: true, slotId };
        }

        // Normal synchronous response
        // Check for text content type
        if (result.data?.textContent || result.data?.contentTypes?.[0] === 'text') {
          updateGenerationSlot(slotId, {
            status: 'completed',
            contentType: 'text',
            textContent: result.data.textContent || '',
            // Carry the DB generation id so the text slot is markable too.
            asyncGenerationId: result.generation_id,
            rawInput: JSON.stringify(result.sentPayload?.inputs || {})
          });

          addSentryBreadcrumb('generation', 'Text generation completed successfully', 'info', {
            workflowId: workflow.id,
            contentType: 'text',
          });

          return { index: i, success: true, slotId };
        }

        // Check if data is already a string array (from WorkflowExecutor)
        const isStringArray = (arr: any): arr is string[] => {
          return Array.isArray(arr) && arr.every(item => typeof item === 'string');
        };

        // Extract image URLs using the same logic as external-call.ts
        const extractImageUrls = (urlData: any[]): string[] => {
          if (!Array.isArray(urlData)) return [];
          return urlData.flatMap(item => {
            // Check if item has files array
            if (item.files && Array.isArray(item.files)) {
              return item.files.map((file: any) => file.url).filter(Boolean);
            }
            // Check if item has images array
            if (item.images && Array.isArray(item.images)) {
              return item.images.map((img: any) => img.url).filter(Boolean);
            }
            // Check if item itself has a url property (for external array)
            if (item.url && typeof item.url === 'string') {
              return [item.url];
            }
            return [];
          });
        };

        // Handle both formats: string[] (from WorkflowExecutor) or object[] (legacy)
        const previewUrls = isStringArray(result.data?.preview)
          ? result.data.preview
          : extractImageUrls(result.data?.preview || []);

        const originalUrls = isStringArray(result.data?.original)
          ? result.data.original
          : extractImageUrls(result.data?.original || []);

        const externalUrls = isStringArray(result.data?.external)
          ? result.data.external
          : extractImageUrls(result.data?.external || []);

        // Extract content types (from new API format)
        const contentTypes = result.data?.contentTypes || [];

        console.log('🖼️ Extracted URLs:', {
          preview: previewUrls,
          original: originalUrls,
          external: externalUrls,
          contentTypes,
          rawData: {
            preview: result.data?.preview,
            original: result.data?.original,
            external: result.data?.external
          }
        });

        // Combine all preview URLs for multiple images support
        const allPreviewUrls = previewUrls.length > 0 ? previewUrls : externalUrls;
        const allOriginalUrls = originalUrls.length > 0 ? originalUrls : (previewUrls.length > 0 ? previewUrls : externalUrls);

        // Use first available URL for backward compatibility
        const previewUrl = allPreviewUrls[0] || null;
        const originalUrl = allOriginalUrls[0] || null;

        // Determine content type - prefer URL detection for video/audio (more reliable)
        const urlContentType = inferContentTypeFromUrl(originalUrl);
        const contentType = (urlContentType?.startsWith('video') || urlContentType?.startsWith('audio'))
          ? urlContentType
          : (contentTypes[0] || urlContentType);

        updateGenerationSlot(slotId, {
          status: 'completed',
          previewUrl,
          originalUrl,
          previewUrls: allPreviewUrls,
          originalUrls: allOriginalUrls,
          contentType,
          // Carry the DB generation id so the slot is markable (like/dislike/approved).
          // Undefined for sync responses that don't return it — no-op in that case.
          asyncGenerationId: result.generation_id,
          rawInput: JSON.stringify(result.sentPayload?.inputs || {})
        });

        addSentryBreadcrumb('generation', 'Generation completed successfully', 'info', {
          workflowId: workflow.id,
          imageCount: allPreviewUrls.length,
          contentType,
        });

        return { index: i, success: true, slotId };
      } else if (result.errors && Array.isArray(result.errors)) {
        // Handle multiple errors case - mark this slot as failed with first error
        const slotError = result.errors[i] || result.errors[0] || 'Generation failed';
        updateGenerationSlot(slotId, {
          status: 'failed',
          error: slotError
        });
        addSentryBreadcrumb('generation', 'Generation failed', 'error', {
          workflowId: workflow.id,
          error: slotError,
        });
        return { index: i, success: false, error: slotError, slotId };
      } else {
        // Use detailed error message from external API if available
        let errorMessage = result.error || 'Generation failed';

        // If we have external API error details, use them
        if (result.externalApiError && result.externalApiError.message) {
          errorMessage = result.externalApiError.message;
        }

        // Check for limit exceeded errors
        if (errorMessage.includes('ORGANIZATION_LIMIT_EXCEEDED')) {
          onLimitExceeded?.('organization');
        }
        errorMessage = formatGenerationError?.(errorMessage) || errorMessage;

        updateGenerationSlot(slotId, {
          status: 'failed',
          error: errorMessage,
          errorDetails: result.errorDetails || undefined
        });
        addSentryBreadcrumb('generation', 'Generation failed', 'error', {
          workflowId: workflow.id,
          error: errorMessage,
        });
        return { index: i, success: false, error: errorMessage, slotId };
      }
    } catch (error) {
      // Handle network or other errors
      let errorMessage = 'Network error';

      if (error.message) {
        errorMessage = error.message;
      }

      updateGenerationSlot(slotId, {
        status: 'failed',
        error: errorMessage
      });
      addSentryBreadcrumb('generation', 'Generation failed with network error', 'error', {
        workflowId: workflow.id,
        error: errorMessage,
      });
      return { index: i, success: false, error: errorMessage, slotId };
    }
  }, [getToken, currentOrganization, selectedCollections, generationSource, formatGenerationError, updateGenerationSlot, startAsyncPolling, onLimitExceeded]);

  const hasActivePollingForGeneration = useCallback((generationId: number): boolean => {
    return Array.from(pollingTasksRef.current.values()).some(
      (task) => task.generationId === generationId
    );
  }, []);

  // Restore active (still-processing) generations from the API and resume
  // polling. Lives in the hook so EVERY page that dispatches generations
  // (classic workbench, Multi) can resume polling after navigation/reload —
  // the unmount cleanup above kills this instance's timers, so whichever page
  // mounts next must call this to pick the work back up.
  // Holds the organization id whose restore is currently in flight (null when
  // idle). Keyed on the ORG, not a bare boolean: the call sites re-fire this on
  // every org change, so a boolean made an A → B switch mid-restore swallow B's
  // own restore entirely (B's generations never resumed until a remount).
  const restoringOrgIdRef = useRef<number | null>(null);

  const restoreActiveGenerations = useCallback(async () => {
    const capturedOrgId = currentOrganization?.id;
    if (!user || !capturedOrgId || restoringOrgIdRef.current === capturedOrgId) return;

    // Set flag to prevent multiple simultaneous attempts for the SAME org
    restoringOrgIdRef.current = capturedOrgId;

    try {
      const token = await getToken({ template: 'withmail' });
      const response = await fetch('/api/workflow/active-generations', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': capturedOrgId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch active generations');
      }

      const { generations } = await response.json();
      // Resolved after unmount → let the next mounted instance restore instead.
      if (!isMountedRef.current) return;

      // Resolved after a tenant switch → these rows belong to the PREVIOUS org.
      // Slots live in the shared WidgetContext, which `WidgetProvider` already
      // cleared for the new tenant, so committing here would re-seed org A's
      // results into org B's workbench and poll them under org B's
      // `X-Organization-Id` — exactly the leak that clear exists to prevent.
      // A `null` org is the routine refresh blip, NOT a switch (same
      // `5 → null → 5` rule as every other org-scoped guard on this branch).
      const orgNow = organizationIdRef.current;
      if (orgNow != null && orgNow !== capturedOrgId) return;

      console.log(`🔄 Restoring ${generations.length} active generations`);

      generations.forEach((gen) => {
        // Validate generation data
        if (!gen.asyncJobId || !gen.id || !gen.settings) {
          console.error('Invalid generation data, skipping:', gen);
          return;
        }

        // Reuse an existing slot for this generation (slots live in the shared
        // WidgetContext and survive navigation — e.g. a slot created on /multi).
        // Without this check every page visit would add a duplicate slot while
        // the original stayed stuck at "processing" forever.
        const existingSlot = generationSlotsRef.current.find(
          (slot) => slot.asyncGenerationId === gen.id
        );

        // Polling counts as active only while its SLOT is still alive. The
        // org-switch clear in `WidgetProvider` wipes `generationSlots` without
        // touching `pollingTasksRef`, so a task outlives its slot by up to one
        // poll interval (it self-deletes on its next tick, on finding the slot
        // gone). Skipping on the task alone made an `A → B → A` round-trip
        // inside that window drop the generation entirely: restore saw the
        // doomed task and returned, then the task died and nothing re-armed it,
        // leaving a DEBITED async generation with neither a slot nor a poller —
        // stuck `pending` (tokens never finalized or refunded, result never
        // surfaced) until the next remount. Falling through instead re-creates
        // the slot and arms a fresh task under a NEW slot id; the stale task
        // still dies on its own next tick.
        if (existingSlot && hasActivePollingForGeneration(gen.id)) return;

        if (existingSlot) {
          setTimeout(() => {
            if (isMountedRef.current) startAsyncPolling(existingSlot.id, gen.id, gen.asyncJobId);
          }, 100);
          return;
        }

        // Build pipeline progress data if available
        const pipelineData: Record<string, any> = {};
        if (gen.pipeline) {
          pipelineData.pipelineSteps = gen.pipeline.steps;
          pipelineData.currentStepIndex = gen.pipeline.currentStep;
          pipelineData.totalSteps = gen.pipeline.totalSteps;
        }

        const slotId = addGenerationSlot({
          status: 'processing',
          prompt: gen.prompt,
          settings: gen.settings,
          asyncJobId: gen.asyncJobId,
          asyncGenerationId: gen.id,
          isFavorite: false,
          workflowId: gen.workflowId,
          metadata: {
            userEmail: user?.primaryEmailAddress?.emailAddress,
            workflowName: gen.workflowTitle,
            createdAt: gen.createdAt
          },
          ...pipelineData
        });

        // Start polling for this generation with a small delay to ensure state is updated
        setTimeout(() => {
          if (isMountedRef.current) startAsyncPolling(slotId, gen.id, gen.asyncJobId);
        }, 100);
      });
    } catch (error) {
      console.error('Failed to restore active generations:', error);
    } finally {
      // Only release the claim if it is still OURS — a restore for a newer org
      // may have taken it over while this one was in flight, and clearing it
      // blindly would let a duplicate restore fire for that newer org.
      if (restoringOrgIdRef.current === capturedOrgId) {
        restoringOrgIdRef.current = null;
      }
    }
  }, [user, currentOrganization, getToken, addGenerationSlot, startAsyncPolling, hasActivePollingForGeneration]);

  return { startAsyncPolling, dispatchGeneration, hasActivePollingForGeneration, restoreActiveGenerations };
}
